import * as React from 'react'

import {
  AppointmentFormTypes,
  AppointmentUtils,
  BookAppointmentFormLayout,
  Components,
  Containers,
} from '@grandvisionhq/appointments'
import { Button, Delayed } from '@grandvisionhq/common'
import { Details, Text } from '@grandvisionhq/elements'
import {
  ComposedForm,
  DateOfBirth,
  FormControl,
  FormTypes,
  PhoneNumberInput,
  PrefixSelect,
  TextArea,
  Util,
} from '@grandvisionhq/forms'
import { Types } from '@grandvisionhq/graphql'
import { useIntl } from '@grandvisionhq/state'

import { consents, optIns } from '../../../configuration'
import { isPostalCode } from '../../../util/form-validation'

const { ConsentsAndOptIns, OptionalFieldset } = Components
const { SignInBox } = Containers

import { countriesOrder } from '../../../configuration/phone-number-input'

const BookAppointmentForm: AppointmentFormTypes.FormComponent = ({
  layout,
  loggedIn,
  onChange,
  submitting = false,
  formAlert = null,
  disableSubmit = false,
  beforeSubmitSlot = null,
  ...rest
}) => {
  const { getLabel, getFormattedLabel } = useIntl()
  const [state, setState] = React.useState<FormTypes.ComposedFormState | undefined>()
  const [showOptIns, setShowOptIns] = React.useState(false)

  const options = React.useMemo(
    () =>
      Util.createSimpleSelectOptions(
        ['mr', 'ms', 'mx'].map((key) =>
          getLabel(`appointments.component.bookAppointmentForm.salutation.${key}`)
        )
      ),
    [getLabel]
  )

  const onChangeHandler = React.useCallback(
    (state: FormTypes.ComposedFormState) => {
      setState(state)
      setShowOptIns(state.formState.consentsAndOptIns?.createAccount?.value === 'true')
      onChange?.(state)
    },
    [onChange]
  )

  const stateData = React.useMemo(
    () => (state ? Util.getValuesFromComposedState(state.formState) : undefined),
    [state]
  )

  const phoneRequired = stateData?.additionalDetails?.preferSms === 'true'

  return (
    <ComposedForm {...rest} className="book-appointment-form" onChange={onChangeHandler}>
      {!loggedIn && <SignInBox />}
      {layout === BookAppointmentFormLayout.CHILD && (
        <OptionalFieldset
          labels={{ legend: getLabel('appointments.component.bookAppointmentForm.legend.child') }}
          active={layout === BookAppointmentFormLayout.CHILD}
        >
          <ComposedForm.Part name="childDetails">
            <FormControl
              {...FormControl.defaultProps}
              id="childFirstName"
              data-t="child-first-name"
              label={getLabel('appointments.component.bookAppointmentForm.label.child.firstName')}
              errorMessage={getLabel(
                'appointments.component.bookAppointmentForm.error.child.firstName'
              )}
            />
            <FormControl
              {...FormControl.defaultProps}
              id="childLastName"
              data-t="child-last-name"
              label={getLabel('appointments.component.bookAppointmentForm.label.child.lastName')}
              errorMessage={getLabel(
                'appointments.component.bookAppointmentForm.error.child.lastName'
              )}
            />
            <DateOfBirth {...FormControl.defaultProps} name="childDateOfBirth" required={true} />
          </ComposedForm.Part>
        </OptionalFieldset>
      )}
      <OptionalFieldset
        labels={{
          legend: getLabel('appointments.component.bookAppointmentForm.legend.personalDetails'),
        }}
        active={layout === BookAppointmentFormLayout.CHILD}
      >
        <ComposedForm.Part name="personalDetails">
          <PrefixSelect
            options={options}
            label={getLabel(
              'appointments.component.bookAppointmentForm.label.personalDetails.salutation'
            )}
            errorMessage={getLabel(
              'appointments.component.bookAppointmentForm.error.personalDetails.salutation'
            )}
            placeholder={getLabel(
              'appointments.component.bookAppointmentForm.placeholder.personalDetails.salutation'
            )}
          />
          <FormControl
            {...FormControl.defaultProps}
            id="firstName"
            data-t="personal-details-first-name"
            label={getLabel(
              'appointments.component.bookAppointmentForm.label.personalDetails.firstName'
            )}
            errorMessage={getLabel(
              'appointments.component.bookAppointmentForm.error.personalDetails.firstName'
            )}
          />
          <FormControl
            {...FormControl.defaultProps}
            id="lastName"
            data-t="personal-details-last-name"
            label={getLabel(
              'appointments.component.bookAppointmentForm.label.personalDetails.lastName'
            )}
            errorMessage={getLabel(
              'appointments.component.bookAppointmentForm.error.personalDetails.lastName'
            )}
          />
          <DateOfBirth {...FormControl.defaultProps} name="dateOfBirth" required={false} />
        </ComposedForm.Part>
      </OptionalFieldset>
      <OptionalFieldset
        labels={{
          legend: getLabel('appointments.component.bookAppointmentForm.legend.contactDetails'),
        }}
        active={layout === BookAppointmentFormLayout.CHILD}
      >
        <ComposedForm.Part name="contactDetails">
          <FormControl
            {...FormControl.defaultProps}
            id="email"
            type="email"
            data-t="contact-details-email"
            tooltip={getLabel(
              'appointments.component.bookAppointmentForm.tooltipText.contactDetails.email'
            )}
            label={getLabel(
              'appointments.component.bookAppointmentForm.label.contactDetails.email'
            )}
            disabled={loggedIn}
            validations={[Util.validations.isEmailAddress]}
            errorMessage={getLabel(
              'appointments.component.bookAppointmentForm.error.contactDetails.email'
            )}
          />
          <PhoneNumberInput
            {...FormControl.defaultProps}
            id="mobilePhone"
            data-t="contact-details-mobile-phone"
            tooltip={getLabel(
              'appointments.component.bookAppointmentForm.tooltipText.contactDetails.phone'
            )}
            label={getLabel(
              'appointments.component.bookAppointmentForm.label.contactDetails.mobilePhone'
            )}
            errorMessage={getLabel(
              'appointments.component.bookAppointmentForm.error.contactDetails.mobilePhone'
            )}
            required={phoneRequired}
            defaultCountry="AT"
            countriesOrder={countriesOrder}
            localeForTranslations="de-AT"
          />
          <FormControl
            {...FormControl.defaultProps}
            className="book-appointment-form__postalCode"
            tooltip={getLabel(
              'appointments.component.bookAppointmentForm.tooltipText.contactDetails.postalCode'
            )}
            id="postalCode"
            data-t="contact-details-postal-code"
            label={getLabel(
              'appointments.component.bookAppointmentForm.label.contactDetails.postalCode'
            )}
            validations={[isPostalCode('AT')]}
            errorMessage={getLabel(
              'appointments.component.bookAppointmentForm.error.contactDetails.postalCode'
            )}
            required={false}
          />
          <Text variant="caption" color="text-secondary">
            {getLabel(
              'appointments.component.bookAppointmentForm.placeholder.contactDetails.postalCode'
            )}
          </Text>
        </ComposedForm.Part>
        <ComposedForm.Part name="additionalDetails">
          <Details
            title={getLabel(
              'appointments.component.bookAppointmentForm.title.additionalDetails.addNote'
            )}
            subtitle={getLabel(
              'appointments.component.bookAppointmentForm.subtitle.additionalDetails.addNote'
            )}
          >
            <TextArea
              className="book-appointment-form__customerComment"
              errorMessage={getLabel(
                'appointments.component.bookAppointmentForm.error.additionalDetails.customerComment'
              )}
              name="customerComment"
              label={getLabel(
                'appointments.component.bookAppointmentForm.label.additionalDetails.customerComment'
              )}
              maxLength={250}
              validations={[Util.validations.maxLength(250)]}
            />
          </Details>
        </ComposedForm.Part>
      </OptionalFieldset>
      {!loggedIn && (
        <ComposedForm.Part name="consentsAndOptIns">
          <ConsentsAndOptIns
            className="book-appointment-form__spacing-top"
            errorMessage={getLabel('appointments.component.consentsAndOptIns.error')}
            consents={consents}
            optIns={optIns}
            showOptIns={showOptIns}
          />
        </ComposedForm.Part>
      )}
      {layout === BookAppointmentFormLayout.CHILD && (
        <Text className="book-appointment-form__spacing-top">
          {getFormattedLabel('appointments.component.bookAppointmentForm.additionalCaption', {
            parseLink: true,
            openLinksInNewTab: true,
          })}
        </Text>
      )}

      {formAlert}
      {beforeSubmitSlot}

      <Button
        type="submit"
        caption={getLabel('appointments.component.bookAppointmentForm.submit')}
        isLoading={submitting}
        disabled={disableSubmit}
        className="book-appointment-form__spacing-top"
      />
      {submitting && (
        <Delayed delay={2500} scrollIntoView className="book-appointment-form__spacing-top">
          <Text
            color="text-secondary"
            align="center"
            className="book-appointment-form__spacing-top"
          >
            {getLabel('appointments.component.bookAppointmentForm.slowLoadingIndication')}
          </Text>
        </Delayed>
      )}
    </ComposedForm>
  )
}

BookAppointmentForm.fromDataToState = (data, {}) => {
  let state: FormTypes.ComposedFormValueState = {}

  const personalDetails: Record<string, string | number> = {
    salutation: data.defaultBillingAddress?.salutation ?? data.personalDetails?.salutation ?? '',
    firstName: data.defaultBillingAddress?.firstName ?? data.personalDetails?.firstName ?? '',
    lastName: data.defaultBillingAddress?.lastName ?? data.personalDetails?.lastName ?? '',
    dateOfBirth: data.personalDetails?.dateOfBirth,
  }

  const contactDetails: Record<string, string | number> = {
    email: data.email,
    mobilePhone: data.personalDetails?.phone ?? '',
    postalCode: data.defaultBillingAddress?.postalCode ?? '',
  }

  state = Util.mergeComposedFormValueState(state, {
    personalDetails: Util.fieldsToFormState(personalDetails),
  })

  state = Util.mergeComposedFormValueState(state, {
    contactDetails: Util.fieldsToFormState(contactDetails),
  })

  return state
}

BookAppointmentForm.getFormGroups = ({ layout }: AppointmentFormTypes.GetFormGroups): string[] =>
  layout === BookAppointmentFormLayout.CHILD
    ? [
        'childDetails',
        'personalDetails',
        'contactDetails',
        'additionalDetails',
        'consentsAndOptIns',
      ]
    : ['personalDetails', 'contactDetails', 'additionalDetails', 'consentsAndOptIns']

BookAppointmentForm.fromStateToData = (state, { layout, intl }, metadata) => {
  const data = Util.getValuesFromComposedState(state)
  /* if one of the optIns exists, then all optIns are displayed
  meaning there are checkboxes configured that are shown to the user */
  const consentsAreDisplayed = data.consentsAndOptIns && 'createAccount' in data.consentsAndOptIns
  const optInsAreDisplayed = data.consentsAndOptIns && 'direct-mail' in data.consentsAndOptIns

  const shouldSubmitOptIns = !metadata?.loggedIn && optInsAreDisplayed

  const optins =
    shouldSubmitOptIns && data.consentsAndOptIns
      ? AppointmentUtils.formatOptIns(intl, data.consentsAndOptIns, optIns, consents)
      : null

  const result: AppointmentFormTypes.FormStateData = {
    child:
      layout === BookAppointmentFormLayout.CHILD
        ? {
            firstName: data.childDetails?.childFirstName ?? '',
            lastName: data.childDetails?.childLastName ?? '',
            dateOfBirth: Util.toStandardDateFormat(data.childDetails?.childDateOfBirth) ?? '',
          }
        : undefined,
    personalDetails: {
      salutation: data.personalDetails?.salutation ?? '',
      firstName: data.personalDetails?.firstName ?? '',
      lastName: data.personalDetails?.lastName ?? '',
      dateOfBirth: Util.toStandardDateFormat(data.personalDetails?.dateOfBirth),
      email: data.contactDetails?.email ?? '',
      phone: data.contactDetails?.mobilePhone ?? '',
      ssn: data.personalDetails?.ppsn ?? null, // This is an example, it's unused for whitelabel
      billingAddress: {
        salutation: data.personalDetails?.salutation ?? '',
        firstName: data.personalDetails?.firstName ?? '',
        lastName: data.personalDetails?.lastName ?? '',
        postalCode: data.contactDetails?.postalCode ?? '',
      },
    },
    optins,
    consents:
      consentsAreDisplayed && !metadata?.loggedIn
        ? [
            {
              consentName: 'terms-conditions',
              value: data.consentsAndOptIns?.createAccount === 'true',
              uiType: Types.UIType.CHECKBOX,
              literalText: intl.getFormattedLabel(
                AppointmentUtils.getLiteralText('createAccount', consents) ?? '',
                { parseHTML: false, parseLink: true }
              ),
            },
          ]
        : null,
    customerComment: data.additionalDetails?.customerComment || null,
    preferredChannelOfCommunication: data.additionalDetails?.preferSms
      ? Types.AppointmentCommunicationChannel.SMS
      : Types.AppointmentCommunicationChannel.EMAIL,
    setPreferredStore: !metadata?.loggedIn ? Boolean(data.additionalDetails?.preferredStore) : null,
  }

  return result
}

export default BookAppointmentForm
